import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PackageSearch, Loader2, Check } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { EASE } from "./Motion";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "America/Argentina/Buenos_Aires" }) : "");

const Route = ({ data }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
    <p className="font-mono text-sm tracking-[0.2em] text-[var(--lime)]">{data.code}</p>
    <p data-testid="track-status" className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-white">{data.status_label}</p>
    {(data.origin || data.destination) && <p className="w-full text-sm text-white/75">{data.origin || "—"} → {data.destination || "—"}</p>}
    {(data.sender || data.recipient) && <p className="w-full text-xs text-white/70">{data.sender && `Envía ${data.sender}`}{data.sender && data.recipient && " · "}{data.recipient && `Recibe ${data.recipient}`}</p>}
  </div>
);

const Steps = ({ data }) => {
  const n = data.steps?.length || 1;
  const edge = 50 / n;
  return (
    <div data-testid="track-result" className="mt-8">
      <Route data={data} />
      {data.cancelled ? <p data-testid="track-cancelled" className="mt-6 rounded-2xl bg-red-500/15 p-4 text-sm font-semibold text-red-200">Este envío fue cancelado.</p> : (
        <div className="relative mt-6 grid gap-1" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
          <div className="absolute top-[15px] h-[2px] bg-white/15" style={{ left: `${edge}%`, right: `${edge}%` }} />
          <motion.div className="absolute top-[15px] h-[2px] bg-[var(--lime)]" style={{ left: `${edge}%` }} initial={{ width: 0 }} animate={{ width: `${(data.step / Math.max(n - 1, 1)) * (100 - 2 * edge)}%` }} transition={{ duration: 1.2, ease: EASE }} />
          {(data.steps ?? []).map((s, i) => (
            <div key={s} className="relative flex flex-col items-center text-center">
              <span className={`grid h-8 w-8 place-items-center rounded-full border-2 ${i <= data.step ? "border-[var(--lime)] bg-[var(--lime)] text-[var(--ink)]" : "border-white/25 bg-[var(--night)] text-white/50"}`}>
                {i <= data.step ? <Check className="h-4 w-4" /> : <span className="text-xs">{i + 1}</span>}
              </span>
              <span data-testid={`track-step-${i}`} className={`mt-3 text-[12px] font-semibold leading-tight md:text-sm ${i <= data.step ? "text-white" : "text-white/60"}`}>{s}</span>
            </div>
          ))}
        </div>
      )}
      {data.events?.length > 0 && (
        <ul data-testid="track-events" className="mt-8 space-y-3 border-t border-white/10 pt-6">
          {[...data.events].reverse().map((ev, i) => (
            <li key={`${ev.at}-${i}`} className="flex justify-between gap-4 text-sm">
              <span className="text-white"><b>{ev.status}</b>{ev.place && ` · ${ev.place}`}{ev.detail && <span className="block text-xs text-white/70">{ev.detail}</span>}</span>
              <span className="shrink-0 font-mono text-xs text-white/70">{fmt(ev.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const TrackTool = ({ initialCode = "" }) => {
  const [code, setCode] = useState(initialCode);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const run = async (c) => {
    setLoading(true); setError(""); setData(null);
    try {
      const res = await api.get(`/parcels/track/${encodeURIComponent(c.trim())}`);
      setData(res.data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialCode) run(initialCode);
  }, [initialCode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="track-tool" className="rounded-[28px] border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl md:p-10">
      <p className="font-display text-3xl text-white md:text-4xl">Seguí tu envío</p>
      <p className="mt-2 text-sm text-white/75">Ingresá el número de orden de tu envío PuntoEnvío.</p>
      <form onSubmit={(e) => { e.preventDefault(); run(code); }} className="mt-6 flex flex-col gap-3 sm:flex-row">
        <input data-testid="track-code-input" value={code} onChange={(e) => setCode(e.target.value)} required placeholder="Número de orden" className="h-14 w-full rounded-2xl border border-white/15 bg-black/30 px-5 font-mono uppercase tracking-widest text-white placeholder:normal-case placeholder:tracking-normal placeholder:text-white/50 focus:border-[var(--lime)] focus:outline-none sm:flex-1" />
        <button data-testid="track-submit-button" disabled={loading} className="flex h-14 items-center justify-center gap-2 rounded-2xl bg-[var(--lime)] px-8 font-bold text-[var(--ink)] transition-transform duration-300 hover:-translate-y-0.5">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <PackageSearch className="h-5 w-5" />} Rastrear
        </button>
      </form>
      {error && <p data-testid="track-error" className="mt-4 text-sm font-medium text-red-300">{error}</p>}
      {data && <Steps data={data} />}
    </div>
  );
};
